import { QueryClient } from '@tanstack/react-query';

import { ISupplierCertificatesTableRow } from '../interfaces/ICertificate';

// Dedicated query key for optimistically-pulled supplier certificates. It is intentionally
// NOT a prefix of ['certificate/supplier'] so that invalidating the real list never touches it.
export const OPTIMISTIC_SUPPLIER_CERTIFICATES_QUERY_KEY = ['supplier-certificates-optimistic'];

// Safety net: drop an optimistic row if the reconciling websocket message never arrives.
export const OPTIMISTIC_SUPPLIER_CERTIFICATE_TTL_MS = 60_000;

// An optimistic row has no `id` (so the table treats it as non-clickable) and carries a stable
// `optimisticKey` we use to remove it again — both on rollback and when the reconciling websocket
// message arrives. The key is derived from certificate type + supplier company name.
export type OptimisticSupplierCertificate = Omit<ISupplierCertificatesTableRow, 'id'> & {
  id?: undefined;
  optimisticKey: string;
};

// Deterministic key shared by the optimistic row and the reconciling websocket message.
export const getOptimisticSupplierCertificateKey = (
  certificateType: string | undefined,
  companyName: string | undefined,
): string => {
  const normalize = (value: string | undefined): string => {
    // Reduce to lowercase alphanumerics joined by dashes. The table feeds this key into the
    // source tooltip's CSS class selector which hates spaces.
    return (value ?? '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  };

  // `__` is a safe separator: normalize() never emits underscores, so it can't collide with the
  // dashes inside either part.
  return `${normalize(certificateType)}__${normalize(companyName)}`;
};

export const getOptimisticSupplierCertificates = (
  queryClient: QueryClient,
): OptimisticSupplierCertificate[] => {
  return (
    queryClient.getQueryData<OptimisticSupplierCertificate[]>(
      OPTIMISTIC_SUPPLIER_CERTIFICATES_QUERY_KEY,
    ) ?? []
  );
};

export const addOptimisticSupplierCertificate = (
  queryClient: QueryClient,
  row: OptimisticSupplierCertificate,
): void => {
  queryClient.setQueryData<OptimisticSupplierCertificate[]>(
    OPTIMISTIC_SUPPLIER_CERTIFICATES_QUERY_KEY,
    (prev) => {
      return [row, ...(prev ?? [])];
    },
  );
};

export const removeOptimisticSupplierCertificate = (
  queryClient: QueryClient,
  optimisticKey: string,
): void => {
  queryClient.setQueryData<OptimisticSupplierCertificate[]>(
    OPTIMISTIC_SUPPLIER_CERTIFICATES_QUERY_KEY,
    (prev) => {
      return (prev ?? []).filter((row) => {
        return row.optimisticKey !== optimisticKey;
      });
    },
  );
};
